import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { SolidButton } from 'components/Button';
import { PackageReferenceData } from 'api/queries/packageTour';
import { brandConfig } from 'utils/brand';
import { departureIcon } from 'assets/svgs/brandIcons';
import { countryName } from 'utils/tourNames';
import FieldTrigger from './FieldTrigger';

type Airport = NonNullable<PackageReferenceData['departureAirports']>[number];

// Transport codes from packageSearchReferenceData: FLIGHT needs departure
// airports, OWN ("own arrangement") is accommodation only.
const FLIGHT = 'FLIGHT';
const OWN = 'OWN';

interface ITransportDropdownProps {
  transportType: string;
  selectedAirports: string[];
  airports: Airport[];
  // Whether the tour operators offer stays without transport (OWN).
  offersOwnTransport: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (type: string, airports: string[]) => void;
}

const TransportDropdown: React.FC<ITransportDropdownProps> = ({
  transportType,
  selectedAirports,
  airports,
  offersOwnTransport,
  isOpen,
  onToggle,
  onClose,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const fieldRef = useRef<HTMLDivElement>(null);
  const [panelTop, setPanelTop] = useState(0);

  useEffect(() => {
    // The field's wrapper, not the button: FieldTrigger puts the button
    // in a positioned wrapper of its own, so the button's offsetTop is 0
    // and a phone's panel opened at the top of the form (WHIT-497).
    if (isOpen && fieldRef.current) {
      const field = fieldRef.current;
      setPanelTop(field.offsetTop + field.offsetHeight + 4);
    }
  }, [isOpen]);

  const airportsByCountry: Record<string, Airport[]> = {};
  for (const a of airports) {
    const country = countryName(a.countryCode, i18n.language) || a.countryCode;
    if (!airportsByCountry[country]) airportsByCountry[country] = [];
    airportsByCountry[country].push(a);
  }

  const firstAirport =
    airports.find(a => a.id === selectedAirports[0])?.name || '';
  const triggerLabel =
    transportType === OWN
      ? t('No transport')
      : selectedAirports.length === 0
        ? t('By plane')
        : selectedAirports.length === 1
          ? t('By plane: {{airport}}', { airport: firstAirport })
          : t('By plane: {{airport}} +{{more}}', {
              airport: firstAirport,
              more: selectedAirports.length - 1,
            });

  const toggleAirport = (id: string) => {
    const next = selectedAirports.includes(id)
      ? selectedAirports.filter(a => a !== id)
      : [...selectedAirports, id];
    onChange(FLIGHT, next);
  };

  return (
    <div ref={fieldRef} className="flex flex-col gap-1 flex-1 min-w-0">
      <FieldTrigger
        label={t('Transport')}
        icon={departureIcon}
        open={isOpen}
        // By plane or no transport, never empty (WHIT-497).
        filled
        onClick={onToggle}>
        <span className="text-black">{triggerLabel}</span>
      </FieldTrigger>

      {/* Full-width panel */}
      {isOpen && (
        <div
          className="pkg-transport-panel absolute left-0 right-0 z-50 bg-white rounded-xl shadow-xl border border-tt-gray-purple max-h-[calc(100vh-8rem)] overflow-y-auto mb-4"
          style={{ top: panelTop }}
        >
          {/* Transport type tabs */}
          <div className="flex border-b border-tt-gray-purple">
            <button
              type="button"
              onClick={() => onChange(FLIGHT, selectedAirports)}
              className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                transportType === FLIGHT
                  ? 'text-tt-dark-purple border-b-2 border-tt-dark-purple bg-tt-verylight-purple'
                  : 'text-gray-500 hover:text-tt-dark-purple'
              }`}
            >
              {brandConfig.searchTabEmoji && '✈ '}
              {t('By plane')}
            </button>
            {offersOwnTransport && (
              <button
                type="button"
                onClick={() => onChange(OWN, [])}
                className={`flex-1 py-3 text-sm font-semibold transition-colors ${
                  transportType === OWN
                    ? 'text-tt-dark-purple border-b-2 border-tt-dark-purple bg-tt-verylight-purple'
                    : 'text-gray-500 hover:text-tt-dark-purple'
                }`}
              >
                🚌 {t('No transport')}
              </button>
            )}
          </div>

          {transportType === FLIGHT && (
            <div className="p-4">
              <div className="flex gap-3 mb-3">
                <button
                  type="button"
                  onClick={() =>
                    onChange(
                      FLIGHT,
                      airports.map(a => a.id),
                    )
                  }
                  className="text-xs text-tt-dark-purple hover:underline font-medium"
                >
                  {t('Select all')}
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => onChange(FLIGHT, [])}
                  className="text-xs text-tt-dark-purple hover:underline font-medium"
                >
                  {t('Clear selection')}
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1">
                {Object.entries(airportsByCountry).map(
                  ([country, countryAirports]) => (
                    <div key={country}>
                      <p className="text-xs text-gray-500 font-semibold mb-1 pt-2">
                        {country}
                      </p>
                      {countryAirports.map(a => (
                        <label
                          key={a.id}
                          className="flex items-center gap-2 py-1 hover:text-tt-dark-purple cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAirports.includes(a.id)}
                            onChange={() => toggleAirport(a.id)}
                            className="accent-tt-yellow h-4 w-4 rounded"
                          />
                          {a.name}
                        </label>
                      ))}
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {transportType === OWN && (
            <div className="p-4 text-sm text-gray-500">
              {t(
                'The search will only show accommodation offers without flights.',
              )}
            </div>
          )}

          <div className="p-3 border-t border-tt-gray-purple flex justify-end">
            <SolidButton onClick={onClose} className="!py-2.5 !px-6">
              {t('Confirm')}
            </SolidButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportDropdown;
